// ---------------------------------------------------------------------------
// The offline basemap (PLAN 7.5, PLAN 12 phase 5).
//
// Imported by both the page and the service worker, so the cache name and what
// counts as a usable copy are stated once rather than twice with a chance to
// drift. Nothing here touches DOM or worker-only globals, which is what lets
// both sides compile it.
//
// The page writes to the same cache the worker reads from, deliberately: a
// volunteer who taps download before the worker has finished registering still
// gets a map.
// ---------------------------------------------------------------------------

import { BUNDLED_PMTILES } from '../lib/config';

/**
 * Bump when the basemap is rebuilt. The archive keeps its URL across rebuilds,
 * so a fresh extract only reaches phones that see a new cache name.
 */
export const BASEMAP_CACHE = 'yucanvas-basemap-v1';

/** True when the archive is on the device and the map will draw with no signal. */
export async function isBasemapCached(): Promise<boolean> {
  if (typeof caches === 'undefined') return false;
  const cache = await caches.open(BASEMAP_CACHE);
  return (await cache.match(BUNDLED_PMTILES)) !== undefined;
}

/**
 * Stores the archive whole -- one 200, no Range header -- because that is the
 * shape the worker's range handler slices from. Storing a partial would be
 * worse than storing nothing: the slicer would treat a fragment as the whole
 * map (see src/sw.ts).
 */
export async function cacheBasemap(): Promise<boolean> {
  if (typeof caches === 'undefined') return false;

  const cache = await caches.open(BASEMAP_CACHE);
  if (await cache.match(BUNDLED_PMTILES)) return true;

  // 'reload' bypasses the HTTP cache, which may hold a partial left by an
  // earlier range request.
  const response = await fetch(BUNDLED_PMTILES, { cache: 'reload' });
  if (response.status !== 200) return false;

  await cache.put(BUNDLED_PMTILES, response);
  return true;
}
